import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';

import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { apiClient } from '../../lib/api-client';
import { useAuth } from '../auth/auth-context';

type Role = 'SUPER_ADMIN' | 'ADWEB_OPERATOR' | 'CLIENT_ADMIN' | 'CLIENT_USER';

interface Org {
  id: string;
  name: string;
  slug: string;
  type: 'ADWEB' | 'CLIENT';
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export function InviteUserModal({ open, onClose }: Props) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<Role>('CLIENT_USER');
  const [organizationId, setOrganizationId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [inviteLink, setInviteLink] = useState<string | null>(null);

  const isPlatform = user?.role === 'SUPER_ADMIN' || user?.role === 'ADWEB_OPERATOR';

  // Liste des organisations pour les rôles plateforme (sinon scopé à user.organizationId)
  const orgs = useQuery({
    queryKey: ['organizations', 'for-invite'],
    queryFn: async () =>
      (await apiClient.get<{ data: Org[] }>('/organizations?pageSize=100')).data.data,
    enabled: isPlatform && open,
  });

  const mutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { email, fullName, role };
      if (isPlatform && organizationId) body.organizationId = organizationId;
      const res = await apiClient.post<{ user: { id: string; email: string } }>(
        '/users/invite',
        body,
      );
      return res.data;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['users'] });
      setEmail('');
      setFullName('');
      setRole('CLIENT_USER');
      setOrganizationId('');
      setError(null);
      // L'API n'expose pas l'URL d'invitation (sécurité), mais on informe.
      setInviteLink(
        "Invitation envoyée. Le lien d'acceptation a été journalisé côté API " +
          '(stub mailer en dev — voir la console du serveur).',
      );
    },
    onError: (err) => {
      const msg =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
        "Échec de l'invitation";
      setError(msg);
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setInviteLink(null);
    mutation.mutate();
  }

  // Les CLIENT_ADMIN ne peuvent pas inviter avec un rôle plateforme ;
  // seul un SUPER_ADMIN peut inviter un autre SUPER_ADMIN.
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const availableRoles: Role[] = isSuperAdmin
    ? ['SUPER_ADMIN', 'ADWEB_OPERATOR', 'CLIENT_ADMIN', 'CLIENT_USER']
    : isPlatform
      ? ['ADWEB_OPERATOR', 'CLIENT_ADMIN', 'CLIENT_USER']
      : ['CLIENT_ADMIN', 'CLIENT_USER'];

  return (
    <Modal
      open={open}
      onClose={() => {
        setInviteLink(null);
        onClose();
      }}
      title="Inviter un utilisateur"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} type="button">
            Annuler
          </Button>
          <Button form="invite-user-form" type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Envoi…' : "Envoyer l'invitation"}
          </Button>
        </>
      }
    >
      <form id="invite-user-form" onSubmit={handleSubmit} className="space-y-4" noValidate>
        {error && (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
          >
            {error}
          </div>
        )}
        {inviteLink && (
          <div
            role="status"
            className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800"
          >
            {inviteLink}
          </div>
        )}
        <div>
          <label htmlFor="invite-email" className="mb-1 block text-xs font-medium text-slate-700">
            Adresse e-mail
          </label>
          <input
            id="invite-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
            placeholder="user@example.com"
          />
        </div>
        <div>
          <label
            htmlFor="invite-fullName"
            className="mb-1 block text-xs font-medium text-slate-700"
          >
            Nom complet
          </label>
          <input
            id="invite-fullName"
            type="text"
            required
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
            placeholder="Jean Dupont"
          />
        </div>
        <div>
          <label htmlFor="invite-role" className="mb-1 block text-xs font-medium text-slate-700">
            Rôle
          </label>
          <select
            id="invite-role"
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
          >
            {availableRoles.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>
        {isPlatform && (
          <div>
            <label htmlFor="invite-org" className="mb-1 block text-xs font-medium text-slate-700">
              Organisation cible
            </label>
            <select
              id="invite-org"
              required
              value={organizationId}
              onChange={(e) => setOrganizationId(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
            >
              <option value="">— choisir —</option>
              {(orgs.data ?? []).map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name} ({o.type})
                </option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-slate-500">
              Les rôles SUPER_ADMIN / ADWEB_OPERATOR doivent être dans l'organisation Adweb.
            </p>
          </div>
        )}
      </form>
    </Modal>
  );
}
